/* ============================================================================
   CHANVRERY — CHAT « ERDY » : PANIER, ATTRIBUTION ET CONFORMITÉ
   ============================================================================

   SOURCE : design/REFERENCE.md (relevé Figma, page 221:6056), design/planches.json,
   chatbot/src/types.ts (ProduitAffichable, EtatErreur, EtatConformite),
   chatbot/src/http/validation.ts, docs/CONTRAT.md §4 « panier ».

   Planches couvertes : D11 (cartes produit dans le fil), G7 (rupture),
   G8 (produit retiré), H1 (18+), H2 (conversation enregistrée),
   H7 (suppression de la conversation).

   ┌───────────────────────────────────────────────────────────────────────┐
   │ POURQUOI L'AJOUT AU PANIER SE FAIT ICI, ET PAS SUR LE SERVEUR         │
   │                                                                       │
   │ Le serveur n'a aucun accès au panier du visiteur : celui-ci vit dans  │
   │ un cookie de première partie que seul ce navigateur porte. Un outil   │
   │ serveur `ajouter_au_panier` qui « réussit » n'ajoute donc rien — il   │
   │ fait seulement dire à Erdy « c'est ajouté ». C'est exactement la       │
   │ sur-promesse que tout le projet cherche à empêcher.                   │
   │                                                                       │
   │ Conséquence : le seul chemin qui ajoute réellement est celui-ci, et   │
   │ il ne rend la main qu'après la réponse de Shopify. Si Shopify refuse  │
   │ (rupture), personne n'annonce un succès.                             │
   └───────────────────────────────────────────────────────────────────────┘

   CE FICHIER NE RÉÉCRIT PAS LA PLOMBERIE DU THÈME.
   L'ajout emprunte le même chemin que `assets/product-form.js` : POST
   /cart/add.js avec la liste des sections à re-rendre, puis
   `cart.renderContents(reponse)` — c'est ce qui met à jour la pastille du
   panier (#cart-icon-bubble) et ouvre le tiroir. Rien n'est recalculé à la
   main : un compteur mis à jour en JavaScript diverge du serveur au premier
   article épuisé.

   ── LIBELLÉS ────────────────────────────────────────────────────────────
   Règle du CONTRAT : aucun texte français en dur dans un .js, tout vient de
   `ChanvreryChat.textes()`. Trois agents écrivant en parallèle, la clé
   `textes().etats` appartient à l'agent 2 (rendu) et n'existe pas encore.
   `TEXTES_SECOURS` ci-dessous est donc lu EN DERNIER RECOURS seulement : dès
   qu'une clé apparaît dans le bloc JSON de la section, elle gagne. Chaque
   libellé de secours est signalé dans le rapport de fin.
   ========================================================================= */

(function () {
  'use strict';

  var Chat = window.ChanvreryChat;

  /* Le socle définit `window.ChanvreryChat` en premier (ordre `defer` imposé
     par la section). S'il manque, c'est que son évaluation a échoué : se taire
     laisserait un bouton AJOUTER inerte sans la moindre trace. */
  if (!Chat) {
    console.error('[Chanvrery · chat] ch-chat.js absent : le module panier ne démarre pas.');
    return;
  }

  /* Évaluation double (section dupliquée dans l'éditeur de thème). */
  if (Chat.module('panier')) return;

  /* ==========================================================================
     1. LIBELLÉS DE SECOURS
     ======================================================================
     ⚠️ INVENTÉS OU RECONSTRUITS. L'extraction Figma tronque les textes à ~32
     caractères (voir docs/CONTRAT.md §7) : les libellés ci-dessous ont dû être
     complétés. Ils sont tous listés dans le rapport de fin, un par un.

     Seuls deux blocs sont VERBATIM et ne doivent pas être réécrits sans
     relecture juridique :
       - H2, dicté mot pour mot par la fiche de cadrage ;
       - « Supprimer la conversation » et « Tu as 18 ans ou plus ? », relevés
         complets dans planches.json.
     ====================================================================== */

  var TEXTES_SECOURS = {
    H1_verification_18: {
      titre: 'Tu as 18 ans ou plus ?', // planches.json H1 — verbatim
      texte: 'La vente de produits CBD est réservée aux personnes majeures.',
      mention: 'Tu confirmes sur l’honneur.',
      oui: 'Oui, j’ai 18 ans ou plus',
      non: 'Non',
      refus: 'La conversation reste fermée : la vente est réservée aux majeurs.',
    },

    /* VERBATIM — texte dicté par la fiche de cadrage, à ne pas reformuler.
       Ce n'est PAS une demande de consentement : la base légale retenue est
       l'intérêt légitime. Aucun bouton « J'accepte » ne doit apparaître ici,
       il laisserait croire au visiteur qu'il peut refuser. */
    H2_consentement_enregistrement: {
      texte:
        'Cette conversation est enregistrée 30 jours pour améliorer le service. ' +
        'Ne partage pas d’informations de santé.',
      lien: 'Lire la politique de confidentialité', // planches.json H2 — verbatim
    },

    H7_suppression_conversation: {
      action: 'Supprimer la conversation', // planches.json H7 — verbatim
      titre: 'Je peux l’effacer tout de suite.',
      rappel: 'Tes commandes et ton compte client ne sont pas concernés.',
      confirmer: 'Supprimer maintenant',
      annuler: 'Annuler',
      faite: 'Conversation effacée.',
      /* Cas du 404 : la route serveur n'existe pas encore (voir le rapport).
         Annoncer « supprimé » serait faux — la transcription reste côté
         serveur pour sa durée de conservation. On le dit. */
      partielle:
        'L’historique affiché a été effacé, mais la demande n’a pas pu être ' +
        'transmise au serveur. Écris-nous pour la faire aboutir.',
    },

    G7_rupture_stock: 'Ce format n’est plus disponible.',
    G8_produit_retire: 'Ce produit n’est plus au catalogue.',
    /* Un ajout peut échouer sans que Shopify en dise la cause. On ne détaille
       pas l'incident : le message d'exception ne doit jamais atteindre le fil. */
    G2_erreur_serveur: 'L’ajout au panier n’a pas marché. Réessaie dans un instant.',

    produit: {
      ajouter: 'Ajouter', // relevé : « bouton AJOUTER », capitalisation laissée au CSS
      /* Suffixe d'unité du prix au gramme, relevé « prix au gramme 13px ». */
      unite_gramme: '/g',
    },
  };

  /* Fusion superficielle : la section (donc l'agent 2) prime toujours. */
  function texte(cle, souscle) {
    var etats = (Chat.textes() || {}).etats || {};
    var fourni = etats[cle];
    var secours = TEXTES_SECOURS[cle];

    if (souscle === undefined) {
      return typeof fourni === 'string' ? fourni : secours;
    }
    if (fourni && typeof fourni === 'object' && typeof fourni[souscle] === 'string') {
      return fourni[souscle];
    }
    return secours ? secours[souscle] : '';
  }

  /* ==========================================================================
     2. STOCKAGE ET COOKIES
     ======================================================================
     Mêmes parades que le socle et que ch-overlay.js : Safari en navigation
     privée fait lever `getItem`. Une exception ici empêcherait l'ajout au
     panier, alors que la persistance n'est qu'un confort.
     ====================================================================== */

  var memoire = {};

  function lireLocal(cle) {
    try {
      var valeur = window.localStorage.getItem(cle);
      if (valeur) return valeur;
    } catch (e) {
      /* Stockage refusé : repli sur la mémoire de page. */
    }
    return memoire[cle] || '';
  }

  function ecrireLocal(cle, valeur) {
    memoire[cle] = valeur;
    try {
      window.localStorage.setItem(cle, valeur);
    } catch (e) {
      /* Le marqueur ne survivra pas au rechargement. Acceptable : le pire cas
         est un bandeau H2 réaffiché une fois de trop. */
    }
  }

  function lireCookie(nom) {
    try {
      var trouve = document.cookie.match(new RegExp('(?:^|;\\s*)' + nom + '=([^;]*)'));
      return trouve ? decodeURIComponent(trouve[1]) : '';
    } catch (e) {
      return '';
    }
  }

  function ecrireCookie(nom, valeur, jours) {
    try {
      var secure = window.location.protocol === 'https:' ? '; Secure' : '';
      document.cookie =
        nom +
        '=' +
        encodeURIComponent(valeur) +
        '; Max-Age=' +
        Math.round(jours * 86400) +
        '; Path=/; SameSite=Lax' +
        secure;
    } catch (e) {
      /* Idem : le choix vaut pour la session en cours. */
    }
  }

  /* Clés partagées avec le reste du thème — voir §5 pour ch_age. */
  var COOKIE_AGE = 'ch_age';
  var JOURS_AGE = 30; /* Défaut de snippets/ch-overlay-age.liquid. */
  var CLE_H2_VU = 'ch_chat_h2_vu';

  /* ==========================================================================
     3. FABRIQUE DOM ET FORMATAGE
     ====================================================================== */

  /**
   * Crée un élément. `textContent` uniquement, jamais `innerHTML` : les noms de
   * produit et les provenances viennent du catalogue Shopify, donc d'un champ
   * saisi à la main. Une apostrophe typographique suffirait à casser un gabarit
   * de chaîne ; une balise ouverte suffirait à faire pire.
   */
  function creer(balise, classe, contenu) {
    var element = document.createElement(balise);
    if (classe) element.className = classe;
    if (contenu !== undefined && contenu !== null && contenu !== '') element.textContent = contenu;
    return element;
  }

  /**
   * Devise de la boutique, dans l'ordre de fiabilité :
   *   1. `ch-toast-region[data-devise]`, rendu par Liquid depuis
   *      `cart.currency.iso_code` (snippets/ch-toast.liquid, toujours présent
   *      via layout/theme.liquid) ;
   *   2. `Shopify.currency.active`, injecté par `content_for_header` ;
   *   3. EUR, la seule devise dans laquelle la boutique vend aujourd'hui.
   */
  function devise() {
    var region = document.querySelector('ch-toast-region');
    if (region && region.dataset.devise) return region.dataset.devise;
    if (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) {
      return window.Shopify.currency.active;
    }
    return 'EUR';
  }

  /**
   * Centimes → « 34,90 € ».
   * ⚠️ Aucun prix n'est calculé ni arrondi : `prix_centimes` et
   * `prix_au_gramme_centimes` arrivent tels quels de Shopify (types.ts). On ne
   * fait que les mettre en forme. Une division par 100 sur un entier ne perd
   * rien ; un calcul de prix au gramme, si.
   */
  function formaterPrix(centimes) {
    if (typeof centimes !== 'number' || !isFinite(centimes)) return '';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'fr', {
        style: 'currency',
        currency: devise(),
      }).format(centimes / 100);
    } catch (e) {
      /* Devise inconnue d'Intl : on rend le nombre plutôt que rien. */
      return (centimes / 100).toFixed(2);
    }
  }

  /* ==========================================================================
     4. CARTES PRODUIT — planche D11
     ======================================================================
     Le relevé reprend le composant « Carte produit » du site avec
     `afficherBadge` et `afficherNote` à false : ni badge, ni étoiles dans le
     chat. Le balisage reste PLAT (pas de div de mise en page) pour que
     component-ch-chat-etats.css puisse le poser en grille sans se battre
     contre des conteneurs qu'il n'a pas demandés.

     `taux` et `provenance` sont RECOPIÉS TELS QUELS de la fiche Shopify :
     ce sont des données contractuelles (types.ts). Jamais reformulés, jamais
     déduits, jamais remplacés par une valeur plausible quand ils valent null —
     dans ce cas la ligne n'existe simplement pas.
     ====================================================================== */

  /**
   * Disponibilité connue, par identifiant de variante, telle que le SERVEUR l'a
   * déclarée (`ProduitAffichable.variantes[].disponible`). Renseignée au rendu.
   *
   * Elle sert au contrat de `ajouter()` : docs/CONTRAT.md §4 exige qu'une
   * variante à `disponible: false` produise G7 — sans aller-retour. Ce n'est pas
   * une optimisation : c'est ce qui garantit le même code d'erreur, que l'appel
   * vienne d'un clic ou d'un autre module.
   */
  var disponibilite = {};

  function variantesUtilisables(produit) {
    return Array.isArray(produit.variantes) ? produit.variantes : [];
  }

  /** Première variante disponible, sinon la première tout court. */
  function varianteParDefaut(variantes) {
    for (var i = 0; i < variantes.length; i++) {
      if (variantes[i] && variantes[i].disponible) return variantes[i];
    }
    return variantes[0] || null;
  }

  function urlProduit(produit) {
    if (!produit.handle) return '';
    return '/products/' + encodeURIComponent(produit.handle);
  }

  /**
   * Applique une variante à une carte : prix, prix au gramme, disponibilité.
   * Appelé au rendu puis à chaque changement du sélecteur de grammage.
   */
  function appliquerVariante(carte, variante) {
    var prix = carte.querySelector('.ch-chat__produit-prix');
    var prixGramme = carte.querySelector('.ch-chat__produit-prix-gramme');
    var bouton = carte.querySelector('.ch-chat__produit-ajouter');

    if (!variante) return;

    carte.dataset.variante = String(variante.id);

    if (prix) prix.textContent = formaterPrix(variante.prix_centimes);

    /* `prix_au_gramme_centimes` vaut null quand Shopify ne permet pas de le
       dériver sans supposition (types.ts). On n'affiche alors RIEN : afficher
       un prix au gramme approché sur un produit vendu au gramme serait une
       information commerciale fausse. */
    if (prixGramme) {
      if (typeof variante.prix_au_gramme_centimes === 'number') {
        prixGramme.hidden = false;
        prixGramme.textContent = formaterPrix(variante.prix_au_gramme_centimes) + texte('produit', 'unite_gramme');
      } else {
        prixGramme.hidden = true;
        prixGramme.textContent = '';
      }
    }

    /* Rupture — planche G7.
       Un bouton simplement désactivé n'explique rien : on l'accompagne de la
       mention. Elle est posée à l'affichage, pas au clic, parce qu'un bouton
       `disabled` ne reçoit pas de clic — le visiteur doit voir POURQUOI il ne
       peut pas acheter avant d'essayer. */
    if (bouton) bouton.disabled = !variante.disponible;

    var mention = carte.querySelector('.ch-chat__etat--g7');
    if (variante.disponible) {
      if (mention) mention.remove();
    } else if (!mention) {
      var bloc = creer('p', 'ch-chat__etat ch-chat__etat--g7');
      bloc.appendChild(creer('span', 'ch-chat__etat-titre', texte('G7_rupture_stock')));
      carte.appendChild(bloc);
    }
  }

  function rendreCarte(produit) {
    var variantes = variantesUtilisables(produit);
    var defaut = varianteParDefaut(variantes);

    variantes.forEach(function (variante) {
      if (variante && variante.id !== undefined) {
        disponibilite[String(variante.id)] = variante.disponible === true;
      }
    });

    var carte = creer('article', 'ch-chat__produit');
    carte.dataset.chChatProduit = '';
    carte.dataset.handle = produit.handle || '';
    carte.dataset.produit = produit.id || '';

    var lien = urlProduit(produit);

    /* --- Visuel : 225 px, fond neutral/100, rayon md ---------------------- */
    var visuel = creer(lien ? 'a' : 'span', 'ch-chat__produit-visuel');
    if (lien) visuel.href = lien;
    if (produit.image_url) {
      var image = creer('img');
      image.src = produit.image_url;
      /* `alt` vide : le nom du produit suit immédiatement en texte. Le répéter
         ferait lire deux fois la même chose au lecteur d'écran. */
      image.alt = '';
      image.loading = 'lazy';
      image.decoding = 'async';
      visuel.appendChild(image);
    }
    carte.appendChild(visuel);

    /* --- Nom : 16 semi-bold ---------------------------------------------- */
    var nom = creer(lien ? 'a' : 'p', 'ch-chat__produit-nom', produit.nom || '');
    if (lien) nom.href = lien;
    carte.appendChild(nom);

    /* --- Provenance : « Indoor · France », 13 neutral/500 ----------------- */
    if (produit.provenance) {
      carte.appendChild(creer('p', 'ch-chat__produit-provenance', produit.provenance));
    }

    /* --- Pastille de taux : « 22 % CBD », pilule surface-muted ------------ */
    if (produit.taux) {
      carte.appendChild(creer('span', 'ch-chat__produit-taux', produit.taux));
    }

    /* --- Sélecteur de grammage : ligne 44, rayon sm, bordure -------------
       Un <select> natif, pas un menu maison : il est atteignable au clavier,
       lu correctement par les lecteurs d'écran, et déclenche la roue système
       sur mobile. Le chevron du relevé est posé en CSS par l'agent 4.
       Une seule variante ⇒ pas de sélecteur : un menu à une entrée est un
       obstacle, pas un choix. */
    if (variantes.length > 1) {
      var selecteur = creer('select', 'ch-chat__produit-grammage');
      selecteur.dataset.chChatGrammage = '';
      /* Le nom du produit désambiguïse deux sélecteurs côte à côte. */
      selecteur.setAttribute('aria-label', produit.nom || '');

      variantes.forEach(function (variante) {
        var option = creer('option', null, variante.grammage || '');
        option.value = String(variante.id);
        /* Variante indisponible : l'option est désactivée plutôt qu'annotée.
           Le navigateur la grise déjà — y accoler « (rupture) » serait un
           texte d'interface inventé pour redire ce que l'état dit. */
        option.disabled = !variante.disponible;
        if (defaut && variante.id === defaut.id) option.selected = true;
        selecteur.appendChild(option);
      });

      selecteur.addEventListener('change', function () {
        var choisie = null;
        for (var i = 0; i < variantes.length; i++) {
          if (String(variantes[i].id) === selecteur.value) choisie = variantes[i];
        }
        appliquerVariante(carte, choisie);
      });

      carte.appendChild(selecteur);
    }

    /* --- Prix 20 bold + prix au gramme 13 neutral/600 --------------------- */
    carte.appendChild(creer('p', 'ch-chat__produit-prix'));
    var prixGramme = creer('p', 'ch-chat__produit-prix-gramme');
    prixGramme.hidden = true;
    carte.appendChild(prixGramme);

    /* --- Bouton AJOUTER : 44, pleine largeur, fond primary ---------------- */
    var bouton = creer('button', 'ch-chat__produit-ajouter', texte('produit', 'ajouter'));
    bouton.type = 'button';
    bouton.dataset.chChatAjouter = '';
    carte.appendChild(bouton);

    appliquerVariante(carte, defaut);
    return carte;
  }

  /* ==========================================================================
     5. AJOUT AU PANIER
     ====================================================================== */

  var enCoursAjout = false;

  function routes() {
    return window.routes || {};
  }

  /**
   * L'élément du thème qui sait re-rendre le panier. Même résolution que
   * `assets/product-form.js` : le tiroir n'existe que si `settings.cart_type`
   * vaut « drawer », la notification que si le thème la rend.
   */
  function contenantPanier() {
    return document.querySelector('cart-notification') || document.querySelector('cart-drawer');
  }

  /**
   * Sections à demander à /cart/add.js pour que Shopify les renvoie rendues.
   * `cart-icon-bubble` y est TOUJOURS : c'est la pastille de l'en-tête, et
   * c'est la seule mise à jour de compteur qui compte pour le visiteur. On la
   * réclame même sans tiroir, sinon un ajout depuis le chat resterait invisible
   * jusqu'au rechargement suivant.
   */
  function sectionsADemander(contenant) {
    var ids = ['cart-icon-bubble'];
    if (contenant && typeof contenant.getSectionsToRender === 'function') {
      contenant.getSectionsToRender().forEach(function (section) {
        if (section && section.id && ids.indexOf(section.id) === -1) ids.push(section.id);
      });
    }
    return ids;
  }

  /** Repli quand aucun tiroir n'existe : on remplace la pastille à la main. */
  function rafraichirPastille(reponse) {
    var html = reponse && reponse.sections && reponse.sections['cart-icon-bubble'];
    if (!html) return;

    var cible = document.getElementById('cart-icon-bubble');
    if (!cible) return;

    try {
      var rendu = new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section');
      if (rendu) cible.innerHTML = rendu.innerHTML;
    } catch (e) {
      /* Section illisible : la pastille attendra le prochain chargement. Un
         compteur en retard vaut mieux qu'une page cassée. */
    }
  }

  /** Diffusion sur le bus du thème (assets/pubsub.js), si présent. */
  function publierAjout(idVariante, reponse) {
    if (typeof window.publish !== 'function' || typeof window.PUB_SUB_EVENTS === 'undefined') return;

    /* `source: 'ch-chat'` et non 'product-form' : snippets/ch-toast.liquid ne
       notifie que les ajouts venus d'une fiche produit. Depuis le chat, le
       tiroir qui s'ouvre EST le retour visuel — un toast par-dessus ferait
       doublon. Les autres abonnés (prix à l'unité, listes de commande) ne
       filtrent pas sur la source et se rafraîchissent quand même. */
    try {
      window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
        source: 'ch-chat',
        productVariantId: idVariante,
        cartData: reponse,
      });
    } catch (e) {
      console.warn('[Chanvrery · chat] diffusion cart-update impossible', e);
    }
  }

  /**
   * Ajoute une variante au panier.
   *
   * @param {string|number} idVariante
   * @param {number} [quantite=1]
   * @returns {Promise<object>} la réponse de /cart/add.js
   *          Rejette avec une Error portant `.etat` : 'G7_rupture_stock' quand
   *          la variante est en rupture, 'G8_produit_retire' quand elle n'est
   *          plus au catalogue, 'G2_erreur_serveur' sinon.
   */
  function ajouter(idVariante, quantite) {
    var nombre = parseInt(quantite, 10);
    if (!isFinite(nombre) || nombre < 1) nombre = 1;

    if (!idVariante) return Promise.reject(echec('G8_produit_retire'));

    /* Rupture déjà connue du serveur : on refuse ici, sans appeler Shopify.
       CONTRAT §4 — « disponible: false ⇒ l'ajout doit produire G7 ». */
    if (disponibilite[String(idVariante)] === false) {
      return Promise.reject(echec('G7_rupture_stock'));
    }

    /* Hors contexte boutique, `window.routes` n'existe pas : mieux vaut un
       refus net qu'un POST vers « undefined.js ». */
    if (!routes().cart_add_url) return Promise.reject(echec('G2_erreur_serveur'));

    var contenant = contenantPanier();

    /* Forme À UN SEUL ARTICLE, et non `{ items: [...] }` : c'est celle que le
       thème utilise (product-form.js) et la seule dont la réponse porte `key`
       et `product_title`. `cart-notification.renderContents` lit `parsedState.key`
       — la forme groupée le laisserait indéfini et casserait le rendu. */
    var corps = {
      id: idVariante,
      quantity: nombre,
      sections: sectionsADemander(contenant).join(','),
      sections_url: window.location.pathname,
    };

    /* Filet de l'attribution : normalement déjà posée au premier message
       (voir §6). Idempotente, donc sans coût si c'est déjà fait. Elle part
       AVANT l'ajout pour que l'attribut existe sur le panier créé par cet
       ajout, y compris si c'est le tout premier article. */
    return marquerPanier()
      .then(function () {
        return fetch(routes().cart_add_url + '.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
          body: JSON.stringify(corps),
        });
      })
      .then(function (reponse) {
        return reponse.json().then(function (donnees) {
          if (reponse.ok) return donnees;
          throw echec(codeDepuisShopify(reponse.status, donnees), donnees);
        });
      })
      .then(function (donnees) {
        if (contenant && typeof contenant.renderContents === 'function') {
          /* Le tiroir se re-rend ET s'ouvre. Planche E7 : la conversation
             reste ouverte derrière, elle n'est ni fermée ni réduite. */
          contenant.renderContents(donnees);
        } else {
          rafraichirPastille(donnees);
        }

        publierAjout(idVariante, donnees);

        /* Compteur de l'étage 1. Échec silencieux voulu (CONTRAT §4) : un
           compteur perdu ne doit pas casser une conversation. */
        var transport = Chat.module('transport');
        if (transport && typeof transport.declarer === 'function') {
          transport.declarer('ajout_panier');
        }

        return donnees;
      });
  }

  function echec(etat, donnees) {
    var erreur = new Error(etat);
    erreur.etat = etat;
    erreur.donnees = donnees || null;
    return erreur;
  }

  /**
   * Traduction des refus de Shopify en codes de planche.
   *
   * ⚠️ On ne lit JAMAIS `donnees.description` pour l'afficher : ce texte vient
   * du thème Shopify, pas du relevé, et il fuiterait un vocabulaire technique
   * dans le fil. Seul le CODE circule ; le texte est choisi par le widget.
   *
   * - 422 : Shopify refuse l'ajout. `status: 422` couvre la rupture (le stock
   *   est le refus de loin le plus fréquent sur une boutique CBD, où les lots
   *   partent vite) — G7.
   * - 404 : la variante n'existe plus. C'est le produit retiré — G8.
   */
  function codeDepuisShopify(statut, donnees) {
    if (statut === 404) return 'G8_produit_retire';
    if (statut === 422) {
      /* Shopify renvoie parfois `description` mentionnant l'inexistence de la
         variante plutôt que le stock. On ne devine pas : le seul indice fiable
         est le champ `status` de Shopify, déjà couvert. */
      return 'G7_rupture_stock';
    }
    if (donnees && donnees.status === 404) return 'G8_produit_retire';
    return 'G2_erreur_serveur';
  }

  /* --- Retour à l'écran lors d'un échec ------------------------------------ */

  /**
   * Affiche l'échec d'un ajout. Passe par le module de rendu quand il existe
   * (c'est lui qui possède le fil) ; sinon pose la mention dans la carte, pour
   * qu'un fichier manquant ne rende pas l'échec invisible.
   */
  function signalerEchec(carte, etat) {
    var libelle = texte(etat) || '';
    var rendu = Chat.module('rendu');

    if (rendu && typeof rendu.afficherEtat === 'function') {
      /* Chemin normal : l'agent 2 possède le fil et tous les libellés G1–G9. */
      rendu.afficherEtat(etat, {});
    } else if (carte && libelle) {
      /* Repli si ch-chat-rendu.js manque : l'échec doit rester visible, sinon
         le visiteur croit son article ajouté. Une seule mention par carte. */
      var existante = carte.querySelector('.ch-chat__etat');
      if (existante) existante.remove();

      var bloc = creer('p', 'ch-chat__etat ch-chat__etat--' + etat.slice(0, 2).toLowerCase());
      bloc.appendChild(creer('span', 'ch-chat__etat-titre', libelle));
      carte.appendChild(bloc);
    }

    if (libelle) Chat.annoncer(libelle);
  }

  /* --- Délégation de clic sur les cartes ----------------------------------- */

  function surClicAjouter(evenement) {
    var bouton = evenement.target.closest('[data-ch-chat-ajouter]');
    if (!bouton) return;

    var carte = bouton.closest('[data-ch-chat-produit]');
    if (!carte) return;

    evenement.preventDefault();

    /* Un seul ajout à la fois : deux clics rapides créeraient deux lignes de
       panier et deux compteurs pour un seul geste. */
    if (enCoursAjout) return;
    enCoursAjout = true;

    /* `aria-busy` pendant l'aller-retour — planche E8. C'est ce qui annonce
       l'attente au lecteur d'écran ; le grisé vient de la feuille des états. */
    bouton.setAttribute('aria-busy', 'true');

    ajouter(carte.dataset.variante, 1)
      .catch(function (erreur) {
        signalerEchec(carte, erreur && erreur.etat ? erreur.etat : 'G2_erreur_serveur');
      })
      .then(function () {
        enCoursAjout = false;
        bouton.removeAttribute('aria-busy');
      });
  }

  /* ==========================================================================
     6. ATTRIBUTION DU CHIFFRE D'AFFAIRES
     ======================================================================
     Dès le PREMIER message du visiteur, le panier reçoit l'attribut `_chat`.
     Il suit le panier jusqu'à la commande ; le webhook `orders/create` le
     relit dans `note_attributes` (chatbot/src/http/attribution.ts) et compte
     la commande comme « le bot a aidé ». Le préfixe souligné le masque au
     client pendant le paiement.

     ── VALEUR POSÉE : « 1 » ────────────────────────────────────────────────
     Le serveur ne teste que la PRÉSENCE de l'attribut :
       `note_attributes.some(a => a.name === ATTRIBUT_CHAT)` — la valeur n'est
     jamais lue. « 1 » suffit donc, et c'est le minimum.

     docs/CONTRAT.md §4 propose d'y écrire `idConversation()`. Écart assumé,
     signalé dans le rapport : inscrire l'identifiant de conversation dans une
     commande Shopify recrée durablement le lien « achat identifié ↔ échange
     conservé » que le serveur a justement démonté (R3 — l'ancien bot
     rattachait les conversations à un identifiant de navigateur en clair). Une
     commande vit des années, une transcription trente jours. La valeur ne
     servant à rien, on ne la pose pas.

     UNE SEULE FOIS PAR SESSION : `POST /cart/update.js` fusionne les attributs
     mais crée un panier côté Shopify. Le refaire à chaque message coûterait un
     aller-retour par tour sans rien changer.
     ====================================================================== */

  var promesseAttribution = null;

  function marquerPanier() {
    if (promesseAttribution) return promesseAttribution;

    var url = routes().cart_update_url;
    if (!url) {
      /* Hors contexte boutique (aperçu, page isolée) : rien à marquer. */
      promesseAttribution = Promise.resolve();
      return promesseAttribution;
    }

    promesseAttribution = fetch(url + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
      body: JSON.stringify({ attributes: { _chat: '1' } }),
    }).catch(function (erreur) {
      /* Échec silencieux : une attribution manquée fausse un tableau de bord,
         elle ne doit pas empêcher une vente. On garde la promesse résolue pour
         ne pas réessayer à chaque message. */
      console.warn('[Chanvrery · chat] attribution du panier impossible', erreur);
    });

    return promesseAttribution;
  }

  /* ==========================================================================
     7. VÉRIFICATION 18+ — planche H1
     ======================================================================
     LE THÈME A DÉJÀ SA BARRIÈRE, ET ELLE FAIT AUTORITÉ.
     `snippets/ch-overlay-age.liquid` + `assets/ch-overlay.js` posent un cookie
     de première partie `ch_age` valant `accepte` ou `refuse`, relu AVANT la
     première peinture et reporté sur `<html data-ch-age>`. La surcouche est
     rendue par layout/theme.liquid sur toutes les pages et pose `inert` sur
     tous ses frères — le chat lui-même est donc inatteignable tant que le
     visiteur n'a pas répondu.

     Conséquence : on RÉUTILISE `ch_age`, on ne redemande pas. Trois cas.

       accepte  → rien à faire.
       refuse   → le widget disparaît. Un mineur ne doit pas pouvoir ouvrir le
                  chat, et la barrière du thème couvre déjà toute la page.
       ni l'un
       ni l'autre
                → si <ch-age-gate> existe, c'est LUI qui pose la question :
                  deux barrières concurrentes donneraient deux réponses
                  possibles pour un même cookie. On se contente de bloquer.
                → s'il n'existe pas (barrière retirée du thème), le chat pose
                  sa propre question — planche H1 — et écrit le MÊME cookie.

     La réponse est mémorisée dans les deux sens : répondre ici vaut pour tout
     le site, répondre au site vaut pour ici.
     ====================================================================== */

  function etatAge() {
    var marque = document.documentElement.dataset.chAge;
    if (marque === 'accepte' || marque === 'refuse') return marque;

    var cookie = lireCookie(COOKIE_AGE);
    if (cookie === 'accepte' || cookie === 'refuse') return cookie;

    return 'demande';
  }

  function barriereDuTheme() {
    return document.querySelector('ch-age-gate');
  }

  var barriere = null;

  function retirerBarriere() {
    if (barriere && barriere.parentNode) barriere.parentNode.removeChild(barriere);
    barriere = null;

    var refs = Chat.refs();
    if (refs.champ) {
      refs.champ.disabled = false;
      try {
        refs.champ.focus({ preventScroll: true });
      } catch (e) {
        refs.champ.focus();
      }
    }
  }

  function confirmerAge(majeur) {
    ecrireCookie(COOKIE_AGE, majeur ? 'accepte' : 'refuse', JOURS_AGE);
    document.documentElement.dataset.chAge = majeur ? 'accepte' : 'refuse';

    /* Le thème écoute cet événement pour révéler le bandeau cookies, qui
       attend derrière la barrière d'âge (ch-overlay.js, surAgeVerifie). */
    if (majeur) document.dispatchEvent(new CustomEvent('ch:age-verifie'));

    if (majeur) {
      retirerBarriere();
      appliquerConformite();
      return;
    }

    /* Refus : la conversation ne s'ouvre pas. On remplace la question par la
       phrase de refus plutôt que de refermer sans rien dire — un panneau qui
       se referme tout seul ressemble à un bug.

       Le champ est désactivé directement, sans passer par
       `verrouillerSaisie('hors_ligne')` : ce verrou-là réécrit la ligne d'aide
       avec « Saisie indisponible hors ligne », ce qui donnerait au visiteur une
       cause fausse. Ici la cause est légale, pas réseau. */
    if (barriere) {
      barriere.textContent = '';
      var carte = creer('div', 'ch-chat__mention ch-chat__mention--h1');
      carte.appendChild(creer('p', 'ch-chat__mention-titre', texte('H1_verification_18', 'refus')));
      barriere.appendChild(carte);
    }
    var champ = Chat.refs().champ;
    if (champ) champ.disabled = true;
    window.setTimeout(fermerDefinitivement, 2500);
  }

  function fermerDefinitivement() {
    Chat.fermer();
    var racine = Chat.racine();
    /* Le lanceur disparaît avec le reste : laisser un bouton cliquable après
       un refus, c'est inviter à réessayer. */
    if (racine) racine.hidden = true;
  }

  function montrerBarriere() {
    if (barriere) return;

    var refs = Chat.refs();
    if (!refs.corps) return;

    barriere = creer('div', 'ch-chat__barriere');
    barriere.dataset.chChatBarriere = '';

    var carte = creer('div', 'ch-chat__mention ch-chat__mention--h1');
    carte.appendChild(creer('p', 'ch-chat__mention-titre', texte('H1_verification_18', 'titre')));
    carte.appendChild(creer('p', 'ch-chat__mention-texte', texte('H1_verification_18', 'texte')));

    var oui = creer('button', 'ch-chat__mention-action', texte('H1_verification_18', 'oui'));
    oui.type = 'button';
    oui.addEventListener('click', function () {
      confirmerAge(true);
    });

    /* « Non » au même gabarit que « Oui », seul l'aplat change : quelqu'un de
       mineur ne doit pas avoir à chercher la sortie. */
    var non = creer('button', 'ch-chat__mention-action ch-chat__mention-action--secondaire', texte('H1_verification_18', 'non'));
    non.type = 'button';
    non.addEventListener('click', function () {
      confirmerAge(false);
    });

    carte.appendChild(oui);
    carte.appendChild(non);

    /* « Tu confirmes sur l'honneur » : la mention du relevé dit exactement ce
       que vaut la déclaration, et évite de faire passer un clic pour un
       contrôle d'identité. */
    carte.appendChild(creer('p', 'ch-chat__mention-note', texte('H1_verification_18', 'mention')));

    barriere.appendChild(carte);

    /* Posée dans le CORPS, pas dans le panneau entier : elle couvre les
       messages et laisse visibles l'en-tête — on doit toujours pouvoir
       refermer — ET le pied légal, qui porte précisément « Vente réservée aux
       18 ans et plus ». Le masquer au moment où l'on demande son âge à
       quelqu'un serait absurde, et contraire au disclaimer permanent.
       Pas dans le fil non plus : c'est une région `log` que `effacerFil()`
       vide, et une barrière légale n'est pas un message. */
    refs.corps.appendChild(barriere);

    /* Tant que l'âge n'est pas confirmé, rien ne part au serveur. */
    if (refs.champ) refs.champ.disabled = true;

    try {
      oui.focus({ preventScroll: true });
    } catch (e) {
      oui.focus();
    }
  }

  /* ==========================================================================
     8. BANDEAU H2 — « conversation enregistrée »
     ======================================================================
     Une fois par visiteur, à la première ouverture.

     ⚠️ CE N'EST PAS UNE DEMANDE DE CONSENTEMENT. La base légale retenue est
     l'intérêt légitime : un bouton « J'accepte » laisserait croire au visiteur
     qu'il peut refuser, alors que l'enregistrement a lieu de toute façon. Il
     n'y a donc ici ni bouton d'acceptation, ni interrupteur — seulement
     l'information et le lien vers la politique.
     ====================================================================== */

  /**
   * Adresse de la politique de confidentialité, reprise du pied légal rendu par
   * la section. On ne fabrique aucune URL : si le marchand n'a rien renseigné
   * et que la boutique n'a pas de politique, la mention reste en texte simple.
   */
  function urlConfidentialite() {
    var legal = Chat.refs().legal;
    if (!legal) return '';
    var liens = legal.querySelectorAll('a[href]');
    /* Le pied légal porte au plus deux liens, « Mentions légales » puis
       « Confidentialité » : le dernier est le bon. */
    return liens.length ? liens[liens.length - 1].getAttribute('href') : '';
  }

  function montrerBandeauH2() {
    if (lireLocal(CLE_H2_VU) === '1') return;

    var refs = Chat.refs();
    if (!refs.fil) return;

    ecrireLocal(CLE_H2_VU, '1');

    var bandeau = creer('div', 'ch-chat__mention ch-chat__mention--h2');
    bandeau.appendChild(creer('p', 'ch-chat__mention-texte', texte('H2_consentement_enregistrement', 'texte')));

    /* Un lien, PAS un bouton, et surtout pas deux : la base légale est
       l'intérêt légitime. Un « J'accepte » laisserait croire au visiteur qu'il
       peut refuser un enregistrement qui a lieu de toute façon. */
    var url = urlConfidentialite();
    if (url) {
      var lien = creer('a', 'ch-chat__mention-lien', texte('H2_consentement_enregistrement', 'lien'));
      lien.href = url;
      bandeau.appendChild(lien);
    }

    /* En TÊTE du fil : la planche H2 montre l'information au-dessus du message
       d'accueil. Le module de rendu ayant déjà posé l'accueil sur `pret`, on
       insère devant plutôt que d'ajouter à la suite. */
    refs.fil.insertBefore(bandeau, refs.fil.firstChild);
  }

  /* ==========================================================================
     9. SUPPRESSION DE LA CONVERSATION — planche H7
     ======================================================================
     ⚠️ LA ROUTE SERVEUR N'EXISTE PAS ENCORE.
     `supprimerConversation()` est écrit et testé côté serveur, mais aucune
     route ne l'appelle (chatbot/ETAT.md §5.e). L'appel ci-dessous vise
     `POST /suppression` avec l'`id_conversation`, comme le prévoit ETAT.md.
     Tant que la route manque, elle répond 404 : on efface alors ce que le
     navigateur détient et on le DIT — annoncer « supprimé » quand la
     transcription reste côté serveur serait exactement le genre de
     sur-promesse que ce projet combat.
     ====================================================================== */

  function urlServeur() {
    return (Chat.reglages() || {}).urlServeur || '';
  }

  /**
   * Demande la suppression au serveur, puis remet la conversation à zéro.
   * @returns {Promise<'supprimee'|'partielle'>}
   */
  function supprimer() {
    /* Capturé AVANT `nouvelleConversation()`, qui régénère l'identifiant. */
    var idConversation = Chat.idConversation();
    var base = urlServeur();

    var appel = base
      ? fetch(base + '/suppression', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id_conversation: idConversation }),
        }).then(function (reponse) {
          /* 404 attendu tant que la route n'est pas livrée. Tout autre échec
             (500, réseau) mène au même aveu : on ne sait pas si c'est parti. */
          return reponse.ok ? 'supprimee' : 'partielle';
        })
      : Promise.resolve('partielle');

    return appel
      .catch(function () {
        return 'partielle';
      })
      .then(function (resultat) {
        /* Le fil local part dans tous les cas : c'est la seule partie que le
           navigateur maîtrise, et le visiteur l'a demandée. */
        Chat.nouvelleConversation();

        var message = texte('H7_suppression_conversation', resultat === 'supprimee' ? 'faite' : 'partielle');
        var rendu = Chat.module('rendu');
        if (rendu && typeof rendu.ajouterMessage === 'function') {
          rendu.ajouterMessage({ role: 'systeme', texte: message });
        }
        Chat.annoncer(message);

        return resultat;
      });
  }

  /** Carte de confirmation — planche H7, posée dans le fil. */
  function demanderSuppression() {
    var refs = Chat.refs();
    if (!refs.fil) return;

    var deja = refs.fil.querySelector('[data-ch-chat-suppression]');
    if (deja) {
      deja.scrollIntoView({ block: 'nearest' });
      return;
    }

    var carte = creer('div', 'ch-chat__mention ch-chat__mention--h7');
    carte.dataset.chChatSuppression = '';
    carte.appendChild(creer('p', 'ch-chat__mention-titre', texte('H7_suppression_conversation', 'titre')));
    carte.appendChild(creer('p', 'ch-chat__mention-note', texte('H7_suppression_conversation', 'rappel')));

    var confirmer = creer('button', 'ch-chat__mention-action', texte('H7_suppression_conversation', 'confirmer'));
    confirmer.type = 'button';
    confirmer.addEventListener('click', function () {
      confirmer.disabled = true;
      supprimer();
    });

    var annuler = creer('button', 'ch-chat__mention-action ch-chat__mention-action--secondaire', texte('H7_suppression_conversation', 'annuler'));
    annuler.type = 'button';
    annuler.addEventListener('click', function () {
      carte.remove();
    });

    carte.appendChild(confirmer);
    carte.appendChild(annuler);
    refs.fil.appendChild(carte);

    var rendu = Chat.module('rendu');
    if (rendu && typeof rendu.defilerEnBas === 'function') rendu.defilerEnBas(true);

    try {
      confirmer.focus({ preventScroll: true });
    } catch (e) {
      confirmer.focus();
    }
  }

  /**
   * Point d'entrée permanent du droit à l'effacement.
   *
   * La section ne rend aucun bouton pour cela et son fichier appartient au
   * socle : la commande est donc ajoutée ici, JUSTE AU-DESSUS du pied légal.
   * Elle ne le masque pas, ne le tronque pas, ne le replie pas — le pied légal
   * reste intact et permanent, comme l'exige la planche H5.
   */
  function poserCommandeSuppression() {
    /* RETIRÉ LE 25/08/2026, à la demande de Julien.
     *
     * Le bouton permanent « Supprimer la conversation » encombrait le bas du
     * panneau pour une action que presque personne n'entreprend, et il
     * appelait `POST /suppression` — une route qui N'EXISTE PAS encore côté
     * serveur (chatbot/ETAT.md §5.e). Il annonçait donc une possibilité qu'il
     * ne tenait pas.
     *
     * CE QUI SUBSISTE : la suppression reste possible quand le VISITEUR la
     * demande — « supprime notre conversation » déclenche la mention H7 venue
     * du serveur, qui porte son propre bouton. Seule la commande permanente
     * disparaît, pas la fonction.
     *
     * ⚠️ La section « chatbot » de la politique de confidentialité annonce
     * « depuis la fenêtre de discussion ». La phrase reste vraie par ce
     * chemin-là ; elle deviendrait fausse si la mention H7 disparaissait
     * aussi. */
    return;
  }

  function poserCommandeSuppressionDesactivee() {
    var refs = Chat.refs();
    if (!refs.legal || !refs.legal.parentNode) return;
    if (refs.legal.parentNode.querySelector('[data-ch-chat-supprimer]')) return;

    var ligne = creer('p', 'ch-chat__conf');
    var bouton = creer('button', 'ch-chat__conf-action', texte('H7_suppression_conversation', 'action'));
    bouton.type = 'button';
    bouton.dataset.chChatSupprimer = '';
    ligne.appendChild(bouton);

    /* JUSTE AU-DESSUS du pied légal, jamais à l'intérieur : celui-ci reste
       intact, entier et permanent (planche H5, CONTRAT §2.2). */
    refs.legal.parentNode.insertBefore(ligne, refs.legal);
  }

  /* Délégation : le module de rendu peut lui aussi poser un bouton
     `[data-ch-chat-supprimer]` dans une mention H7 venue du serveur. Un seul
     écouteur les couvre tous, quel que soit l'agent qui les a créés. */
  function surClicSupprimer(evenement) {
    var bouton = evenement.target.closest('[data-ch-chat-supprimer]');
    if (!bouton) return;
    evenement.preventDefault();
    demanderSuppression();
  }

  /* ==========================================================================
     10. ACCROCHE PROACTIVE — planche A3
     ======================================================================
     RIEN À FAIRE ICI, ET C'EST VOULU.

     Le socle tient déjà l'accroche en entier (assets/ch-chat.js,
     `programmerAccroche`) : délai d'inactivité issu du réglage de section
     (20 s par défaut, valeur du relevé), restriction aux fiches produit,
     affichage une seule fois par session, et cookie `ch_chat_accroche` de
     7 jours posé par la croix (`rejeterAccroche`). docs/CONTRAT.md §6 le range
     explicitement dans le périmètre du socle.

     Le réimplémenter ici donnerait deux minuteries concurrentes sur le même
     état et deux cookies pour une seule décision. Vérifié, laissé au socle.
     ====================================================================== */

  /* ==========================================================================
     11. AMORÇAGE
     ====================================================================== */

  /** Applique les écrans de conformité dus à l'ouverture courante. */
  function appliquerConformite() {
    var age = etatAge();

    if (age === 'refuse') {
      fermerDefinitivement();
      return;
    }

    if (age === 'demande') {
      /* La barrière du thème pose déjà la question sur toute la page : on ne
         la double pas, on attend sa réponse. */
      if (barriereDuTheme()) {
        Chat.fermer();
        return;
      }
      montrerBarriere();
      return;
    }

    montrerBandeauH2();
  }

  Chat.ecouter('pret', function (contexte) {
    var racine = contexte.racine;
    if (!racine) return;

    /* Un seul écouteur de clic pour tout le module : les cartes produit et les
       mentions sont créées après coup, la délégation évite d'avoir à les
       recâbler à chaque insertion. */
    racine.addEventListener('click', function (evenement) {
      if (!(evenement.target instanceof Element)) return;
      surClicAjouter(evenement);
      surClicSupprimer(evenement);
    });

    poserCommandeSuppression();

    /* Refus déjà exprimé sur le site : le widget ne s'affiche pas du tout. */
    if (etatAge() === 'refuse') fermerDefinitivement();

    /* Réponse donnée à la barrière du thème pendant que le chat est chargé. */
    document.addEventListener('ch:age-verifie', function () {
      if (barriere) retirerBarriere();
    });
  });

  /* Les écrans de conformité se jouent à l'OUVERTURE, pas au chargement : H1
     est « avant la première ouverture », H2 « à la première ouverture ». */
  Chat.ecouter('ouvert', appliquerConformite);

  /* Attribution dès le premier message du visiteur — §6. */
  Chat.ecouter('envoi', function () {
    marquerPanier();
  });

  /* ==========================================================================
     12. ENREGISTREMENT
     ====================================================================== */

  Chat.enregistrer('panier', {
    /**
     * Construit le bloc de cartes produit à insérer dans le fil (planche D11).
     * Deux cartes côte à côte, écart 8 — la mise en page est à l'agent 4.
     *
     * @param {Array<object>} produits  ProduitAffichable[] (chatbot/src/types.ts)
     * @returns {HTMLElement}
     */
    rendreProduits: function (produits) {
      var bloc = creer('div', 'ch-chat__produits');
      bloc.dataset.chChatProduits = '';

      (Array.isArray(produits) ? produits : []).forEach(function (produit) {
        if (!produit || !variantesUtilisables(produit).length) return;
        bloc.appendChild(rendreCarte(produit));
      });

      return bloc;
    },

    ajouter: ajouter,

    /* --- Extensions hors CONTRAT §4, signalées dans le rapport ------------- */

    /** Planche H7. @returns {Promise<'supprimee'|'partielle'>} */
    supprimer: supprimer,

    /** Planche H1. @returns {boolean} */
    ageConfirme: function () {
      return etatAge() === 'accepte';
    },
  });
})();
